import { api } from "@/lib/api/client";
import { commitOptions, retryOptions } from "@/lib/api/commitSeams";
import type { SemesterStatus } from "@/lib/api/academicStructure";
import type { ResultLine } from "@/components/ui/box.schema";
import { withVersion } from "@/lib/api/conflict";
import type { RegisterWrite } from "@/lib/api/gradebook";

export type EnrollmentStatus =
  | "enrolled"
  | "withdrawn"
  | "incomplete"
  | "complete";

export interface AddAuditorBody {
  user_id?: string | null;
  legal_name: string;
  email?: string | null;
  preferred_name?: string | null;
  honorific?: string | null;
  gender?: "M" | "F" | null;
}

export interface RosterRow {
  /** Exact tenant-owned person; absent from older servers or retained receipts. */
  person_id?: string | null;
  updated_at?: string;
  id: string;
  status: EnrollmentStatus;
  participation_kind: "credit" | "audit";
  withdrawal_reason: string | null;
  student_id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  created_batch_id: string | null;
  source_kind: "cohort" | "individual" | "auditor" | "historical_import" | null;
  source_cohort_id: string | null;
  initial_notice_id?: string | null;
  initial_notice_state?: EnrollmentNoticeState | null;
}

export interface RosterStudent {
  person_id?: string | null;
  student_id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  program_id: string | null;
  program_name: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  enrollment_id: string | null;
  source_kind: "cohort" | "individual" | null;
  source_cohort_id: string | null;
  status?: string;
  enrollment_status?: EnrollmentStatus | null;
  participation_kind?: "credit" | "audit" | null;
}

export interface RosterWarning {
  code: "program_mismatch";
  message: string;
  student: RosterStudent;
}

export interface RosterPlan {
  cohort_ids: string[];
  student_ids: string[];
  reinstate_enrollment_ids: string[];
  capacity_override_reason: string | null;
}

export interface RosterResult {
  command_key?: string;
  delivery?: {
    state: "confirmed" | "partial" | "unknown";
    succeeded: ResultLine[];
    failed: ResultLine[];
    retry_versions?: Record<string, string>;
  };
  batch_id: string | null;
  added: RosterStudent[];
  already_enrolled: RosterStudent[];
  inactive: RosterStudent[];
  withdrawn_requires_reinstatement: RosterStudent[];
  reinstated: RosterStudent[];
  warnings: RosterWarning[];
  enrolled_before: number;
  enrolled_after: number;
  capacity: number | null;
  remaining_capacity: number | null;
  capacity_override_used: boolean;
  notice_effect?: {
    kind: "attempt_on_apply" | "deferred_until_publication" | "none";
    count: number;
  };
}

export interface Enrollment {
  id: string;
  course_id: string;
  student_id: string;
  status: EnrollmentStatus;
  participation_kind: "credit" | "audit";
  withdrawal_reason: string | null;
  withdrawn_at: string | null;
  enrolled_at: string;
}

export interface TeachingRosterRow {
  enrollment_id: string;
  student_id: string;
  status: EnrollmentStatus;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  display_name: string;
}

export interface PatchEnrollmentBody {
  status?: EnrollmentStatus;
  participation_kind?: "credit" | "audit";
  withdrawal_reason?: string;
  capacity_override_reason?: string;
  idempotency_key?: string;
}

export interface MyEnrollment {
  id: string;
  status: EnrollmentStatus;
  participation_kind: "credit" | "audit";
  code: string;
  title: string;
  meeting_days: string | null;
  meeting_time: string | null;
}

export type EnrollmentNoticeState =
  | "pending"
  | "sending"
  | "accepted"
  | "failed"
  | "not_configured"
  | "uncertain";

export interface EnrollmentNotice {
  notice_id: string;
  updated_at?: string;
  course_id: string;
  enrollment_id: string;
  state: EnrollmentNoticeState;
  reason: string | null;
  retry_eligible: boolean;
  learner_name?: string | null;
  course_code?: string | null;
  course_title?: string | null;
  term_name?: string | null;
  schedule_label?: string | null;
  recipient_source?: "invitation" | "application" | "registrar_roster" | null;
  attempt_count?: number;
  claimed_at?: string | null;
  accepted_at?: string | null;
  provider_ref?: string | null;
}

export interface RosterState {
  rows: RosterRow[];
  updated_at: string;
  term_status: SemesterStatus | null;
  ranks: {
    state: string;
    value?: { candidates: { id: string; rank: number | null; eligible: boolean; blocked: boolean; read: string }[] };
  };
}

export const enrollmentsApi = {
  readRoster: (courseId: string) =>
    api.get<RosterState>(`/courses/${courseId}/roster-state`),
  addAuditor: (courseId: string, body: AddAuditorBody, write?: RegisterWrite) =>
    api.post<RosterRow>(
      `/courses/${courseId}/auditors`,
      write ? withVersion(body, write.version) : body,
      write ? commitOptions(write.key) : {},
    ),
  patch: (
    enrollmentId: string,
    body: PatchEnrollmentBody,
    write?: RegisterWrite,
  ) =>
    api.patch<Enrollment>(
      `/enrollments/${enrollmentId}`,
      write ? withVersion(body, write.version) : body,
      write ? commitOptions(write.key) : {},
    ),
  roster: (courseId: string) =>
    api.get<RosterRow[]>(`/courses/${courseId}/roster`),
  teachingRoster: (courseId: string) =>
    api.get<TeachingRosterRow[]>(`/courses/${courseId}/teaching-roster`),
  candidates: (
    courseId: string,
    q = "",
    limit = 40,
    studentId?: string | null,
  ) => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (studentId) params.set("student_id", studentId);
    return api.get<RosterStudent[]>(
      `/courses/${courseId}/roster/candidates?${params.toString()}`,
    );
  },
  preview: (courseId: string, plan: RosterPlan) =>
    api.post<RosterResult>(`/courses/${courseId}/roster/preview`, plan),
  batch: (courseId: string, batchId: string) =>
    api.get<RosterResult>(
      `/courses/${courseId}/roster/batches/${encodeURIComponent(batchId)}`,
    ),
  retryBatch: (courseId: string, batchId: string, key: string, noticeVersions: Record<string, string>) =>
    api.post<RosterResult>(
      `/courses/${courseId}/roster/batches/${encodeURIComponent(batchId)}/retry`,
      { notice_versions: noticeVersions },
      retryOptions(key),
    ),
  apply: (
    courseId: string,
    plan: RosterPlan,
    idempotencyKey: string,
    version?: string,
  ) =>
    api.post<RosterResult>(
      `/courses/${courseId}/roster/apply`,
      {
        ...plan,
        idempotency_key: idempotencyKey,
        expected_updated_at: version,
      },
      commitOptions(idempotencyKey),
    ),
  notice: (noticeId: string) =>
    api.get<EnrollmentNotice>(
      `/enrollment-notices/${encodeURIComponent(noticeId)}`,
    ),
  retryNotice: (noticeId: string, write?: RegisterWrite) =>
    api.post<EnrollmentNotice>(
      `/enrollment-notices/${encodeURIComponent(noticeId)}/retry`,
      write ? withVersion({}, write.version) : {},
      write ? commitOptions(write.key) : {},
    ),
  my: () => api.get<MyEnrollment[]>("/enrollments/my"),
};
